import { UserFormQuestionType } from '@hiwaldo/sdk';
import { UserFormQuestion } from '@hiwaldo/sdk/interfaces';
import { useEffect, useState } from 'react';
import {
  FormGroup,
  useFormFieldEffect,
  FormField,
  FormFieldType,
  FormFieldValue,
} from '../..';
import { validateRequired } from '../../validators';

export const useUserFormEffect = (questions: UserFormQuestion[]): FormGroup => {
  const [isValid, setValid] = useState(false);
  const [isClean, setClean] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);

  let members: { [key: string]: FormField } = {};

  questions.forEach((question) => {
    const name = question.title.replace(/\s/g, '-').toLocaleLowerCase();
    const dateInput = new Date();
    let type: FormFieldType;
    let value: FormFieldValue;

    switch (question.questionType) {
      case UserFormQuestionType.MULTIPLE_CHOICE:
        type = 'checkbox';
        value = [] as number[];
        break;
      case UserFormQuestionType.SINGLE_OPTION:
        type = 'select';
        value = null;
        break;
      case UserFormQuestionType.TRUE_OR_FALSE:
        type = 'radiobox';
        value = false;
        break;
      case UserFormQuestionType.DATE_INPUT:
        type = 'date';
        value = dateInput;
        break;
      default:
        type = 'text';
        value = '';
    }

    const choices = question.choices.length
      ? question.choices.map((choice, key) => {
          return {
            key,
            value: `${choice.id}`,
            label: choice.phrase,
          };
        })
      : undefined;

    const field = useFormFieldEffect(
      name,
      type,
      [validateRequired],
      {
        required: true,
      },
      value,
      choices,
      question.phrase
    );

    members = { ...members, [name]: field };
  });

  useEffect(() => {
    let valid = true;
    let clean = true;

    const formFields = members as any as { [key: string]: FormField };

    for (const i in formFields) {
      if (!formFields[i].isValid) {
        valid = false;
      }

      if (!formFields[i].isClean) {
        clean = false;
      }
    }

    if (isValid !== valid) {
      setValid(valid);
    }

    if (isClean !== clean) {
      setClean(clean);
    }
  }, [members, isClean, setClean, isValid, setValid]);

  const getFieldValues = (): any => {
    let values = {};

    for (const i in members) {
      const member = members[i];

      switch (member.type) {
        case 'checkbox':
          values = {
            ...values,
            [member.name]: (member.value as number[]) || null,
          };
          break;
        case 'select':
          values = {
            ...values,
            [member.name]: member.value as number,
          };
          break;
        case 'radiobox':
          values = {
            ...values,
            [member.name]: !!member.value as boolean,
          };
          break;
        case 'date':
          values = {
            ...values,
            [member.name]: (member.value as Date)
              .toISOString()
              .replace(' GMT', ''),
          };
          break;
        default:
          values = {
            ...values,
            [member.name]: (member.value as string) || null,
          };
      }
    }

    return values;
  };

  const resetFieldValues = (clean = false): any => {
    for (const i in members) {
      members[i].doReset(clean);
    }
  };

  return {
    isValid,
    isClean,
    isSubmitted,
    setValid,
    setClean,
    setSubmitted,
    getFieldValues,
    resetFieldValues,
    members,
  };
};
